import { CategoricalChartState } from 'recharts/types/chart/types'
import { ChartInstanceRegistry, LineChartStore } from '../store'
import { AI360ChartData } from '../types'
import { EmsBatteryMode, EmsSgReadyMode } from 'src/Libs/dynamic-power-prices/types'

/**
 * AI360 line chart data has data points for every 15 minutes
 */
export const getCurrentLineChartDataIndex = (time: Date): number => {
  const hours = time.getHours() // 0 - 23
  const minutes = time.getMinutes() // 0 - 59

  // Calculate index: 4 points per hour + 1 point per 15 minutes
  const index = hours * 4 + Math.floor(minutes / 15)
  return index
}

/**
 * sets the tooltip/legend state to the data point matching the current time.
 * returns whether a matching data point was found and the store was actually updated.
 */
export const setLineChartStoreToCurrentTime = (chartData: AI360ChartData[]): boolean => {
  const index = getCurrentLineChartDataIndex(new Date())
  const currentLineChartData = chartData[index]

  if (!currentLineChartData) return false

  LineChartStore.set({
    time: currentLineChartData.time,
    powerPrice: currentLineChartData.powerPrice,
    feedInPrice: currentLineChartData.feedInPrice,
    solarProduction: currentLineChartData.solarProduction,
    solarForecast: currentLineChartData.solarForecast,
    emsBatteryMode: currentLineChartData.emsBatteryMode ?? EmsBatteryMode.NormalOperation,
    emsSgReadyMode: currentLineChartData.emsSgReadyMode ?? EmsSgReadyMode.Normal,
    defaultDataIndex: index,
  })
  return true
}

export const onMouseMoveAI360Chart = (state: CategoricalChartState, lineChartData: AI360ChartData[]): void => {
  const { activeTooltipIndex, isTooltipActive } = state

  if (isTooltipActive && activeTooltipIndex !== undefined) {
    const activeData = lineChartData[activeTooltipIndex]
    if (!activeData) return

    LineChartStore.set({
      time: activeData.time,
      powerPrice: activeData.powerPrice,
      feedInPrice: activeData.feedInPrice,
      solarProduction: activeData.solarProduction,
      solarForecast: activeData.solarForecast,
      emsBatteryMode: activeData.emsBatteryMode ?? EmsBatteryMode.NormalOperation,
      emsSgReadyMode: activeData.emsSgReadyMode ?? EmsSgReadyMode.Normal,
      defaultDataIndex: null,
    })
  }
}

/**
 * once the mouse leaves any of the synced AI360 charts, snap the cursor/legends back to the current time
 * on all of them, instead of leaving the last hovered value stuck or resetting to a blank default.
 */
export const onMouseLeaveAI360Chart = (chartData: AI360ChartData[]): void => {
  setLineChartStoreToCurrentTime(chartData)
  ChartInstanceRegistry.resetAllToDefaultTooltip()
}

/**
 * Identifies continuous ranges of data points where `feedInPrice` is zero.
 */
export const getZeroFeedInPriceRanges = (data: AI360ChartData[]): { x1: number; x2: number }[] => {
  const ranges: { x1: number; x2: number }[] = []
  let start: number | null = null

  for (const { time, feedInPrice } of data) {
    if (!time) continue

    const isZeroFeedInPrice = feedInPrice === 0

    /* start a new range with zero feed-in price */
    if (isZeroFeedInPrice && start === null) {
      start = time
      continue
    }

    /* end the current range of contiguous zero feed-in price */
    if (!isZeroFeedInPrice && start !== null) {
      ranges.push({ x1: start, x2: time })
      start = null
    }
  }

  /* close any open range that extends to the end of the data */
  const lastTime = data.at(-1)?.time
  if (start !== null && lastTime != null) {
    ranges.push({ x1: start, x2: lastTime })
  }

  return ranges
}
